import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { supabase } from '../services/supabaseClient/supabaseClient';
import { fetchCoursesByUserId } from '../services/supabaseClient/courseService';
import { getOrCreateProfile } from '../services/supabaseClient/profileService';
import { setCourses } from './coursesSlice';
import { setStudentProfile } from './profileSlice';

// 🔁 Load Auth + Profile + Courses
export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await supabase.auth.getSession();
      const user = data?.session?.user;

      if (user) {
        dispatch(setUser(user));

        // ✅ 1️⃣ Fetch and persist profile
        const { data: profile, error: profileError } = await getOrCreateProfile(user.id);
        if (profileError) return rejectWithValue(profileError.message);

        dispatch(setStudentProfile(profile));
        localStorage.setItem('profile', JSON.stringify(profile));

        // ✅ 2️⃣ Fetch and store user-owned courses
        const { data: courses, error: courseError } = await fetchCoursesByUserId(user.id);
        if (courseError) return rejectWithValue(courseError.message);

        dispatch(setCourses(courses));

        // ✅ 3️⃣ Check if user was trying to view a shared course
        const sharedCourseId = localStorage.getItem('sharedCourseId');
        if (sharedCourseId) {
          localStorage.removeItem('sharedCourseId');

          const currentPath = window.location.pathname;
          const expectedPath = `/courses/${sharedCourseId}`;

          if (currentPath !== expectedPath) {
            window.location.href = expectedPath; // full reload
            return;
          }
        }
      } else {
        dispatch(clearUser());
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 🧠 Auth Slice
const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    loading: true,
  },
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.loading = false;
    },
    clearUser: (state) => {
      state.user = null;
      state.loading = false;
      localStorage.removeItem('profile');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeAuth.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.loading = false;
      });
  },
});

// ✅ Export Actions + Selectors
export const { setUser, clearUser } = authSlice.actions;
export const selectUser = (state) => state.auth.user;
export const selectAuthLoading = (state) => state.auth.loading;

// ✅ Get just the current authenticated user's ID
export const selectAuthUserId = (state) => state.auth.user?.id ?? null;

export default authSlice.reducer;
